import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'carbon-components-react';
import {
  AreaCustom16,
  RadioButton16,
  FlowData16,
  Maximize16
} from '@carbon/icons-react';
import { useSelector } from 'react-redux';
import { selectScreenSize } from 'redux/session/selectors';
import { SCREEN_SIZE } from 'types';
import ShowRecentsToggle from '../showRecentsToggle';

const getInstruction = (tool: string) => {
  switch (tool) {
    case 'circle':
      return 'Click and drag to draw then release to finish';
    case 'polyline':
      return 'Double-click to finish line';
    default:
      return 'Double-click to close shape';
  }
};

const MapTools: React.FC<Props> = ({
  toolExpanded,
  activeTool,
  allButtonDisabled,
  onExpandTools,
  onClickTools
}) => {
  const screenSize = useSelector(selectScreenSize);

  return (
    <div className="map-tools-container">
      {toolExpanded && (
        <div className="map-tools-search-bg">
          <div className={`map-tools-search-icon`}>OR</div>
        </div>
      )}
      {toolExpanded && <div className="map-tools-divider" />}
      {!toolExpanded && (
        <div className="map-tools-expander" onClick={onExpandTools}>
          <Maximize16 />
        </div>
      )}
      <div className="map-tools-draw-control-container">
        {toolExpanded && (
          <div className="map-tools-draw-control-label">
            {activeTool !== 'selector'
              ? getInstruction(activeTool)
              : 'Draw your job site'}
          </div>
        )}
        <div className="map-tools-draw-control-buttons">
          <Button
            renderIcon={AreaCustom16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="SHAPE"
            tooltipPosition="bottom"
            disabled={allButtonDisabled}
            className={`${activeTool === 'polygon' ? ' active-map-tool' : ''}`}
            onClick={() => onClickTools('polygon')}
          />
          <Button
            renderIcon={FlowData16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="LINE"
            tooltipPosition="bottom"
            disabled={allButtonDisabled}
            className={`${activeTool === 'polyline' ? ' active-map-tool' : ''}`}
            onClick={() => onClickTools('polyline')}
          />
          <Button
            renderIcon={RadioButton16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="CIRCLE"
            tooltipPosition="bottom"
            disabled={allButtonDisabled}
            className={`${activeTool === 'circle' ? ' active-map-tool' : ''}`}
            onClick={() => onClickTools('circle')}
          />
        </div>
        {!toolExpanded && (
          <div className="map-tools-draw-control-instruction">
            {getInstruction(activeTool)}
          </div>
        )}
      </div>
      <div className="map-tools-divider" />
      {![SCREEN_SIZE.MD, SCREEN_SIZE.SM].includes(screenSize) && (
        <div className="map-recent-container">
          <ShowRecentsToggle noTooltip={true} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  toolExpanded: PropTypes.bool,
  activeTool: PropTypes.string.isRequired,
  allButtonDisabled: PropTypes.bool,
  removeButtonDisabled: PropTypes.bool,
  onClickTools: PropTypes.func.isRequired,
  onExpandTools: PropTypes.func.isRequired
};

type Props = PropTypes.InferProps<typeof propTypes>;

MapTools.propTypes = propTypes;

MapTools.defaultProps = {
  toolExpanded: false,
  activeTool: 'selector',
  removeButtonDisabled: false,
  allButtonDisabled: false
};

MapTools.displayName = 'LodgeEnquiryMapTools';

export default React.memo(MapTools);
